import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import { button } from "./style";

/**
 * Forgot password page for student/parent/tutor/admin portal.
 * Asks for email and sends reset link (or shows success message).
 */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email?.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("forgot-password", { email: email.trim() });
      setSubmitted(true);
      toast.success("If an account exists with this email, we've sent password reset instructions.");
    } catch (err) {
      const msg = err.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container sx={{ height: "100vh" }}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundImage: `url("/assets/images/signup-bg.png")`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          py: 9,
        }}
      >
        <img
          src="/assets/images/hero-right-img.png"
          alt="Character"
          style={{ width: "80%", maxWidth: "300px" }}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          backgroundColor: "#fff",
          py: 6,
          px: { xs: 4, md: 6 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Box sx={{ mb: 4 }}>
            <img src="/assets/images/logo.svg" alt="Logo" />
          </Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
            <Box component="span" sx={{ color: "#D6232A" }}>Forgot password?</Box>
          </Typography>
          {submitted ? (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                If an account exists with the email you entered, we've sent instructions to reset your password. Please check your inbox (and spam folder).
              </Typography>
              <Button
                component={Link}
                to="/login"
                startIcon={<ArrowBackIcon />}
                sx={{ color: "#1565c0", fontWeight: 600 }}
              >
                Back to login
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              <Box component="form" onSubmit={onSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  placeholder="Enter your email"
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
                  <Button
                    type="submit"
                    disableElevation
                    disabled={loading}
                    sx={{
                      ...button,
                      backgroundColor: "#EF2A1E",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#EF2A1E" },
                    }}
                  >
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    <Link to="/login" style={{ color: "#1565c0", fontWeight: 600 }}>Back to login</Link>
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
